import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  inject
} from '@angular/core'
import {
  Observable,
  Subject,
  Subscription,
  EMPTY,
  of,
  concat,
  timer,
  ReplaySubject,
  combineLatest,
  BehaviorSubject,
  merge
} from 'rxjs'
import { UrlCollection } from '../../../../url-collection'
import * as fromConfigSelectors from '../../../../root-store/config-store/config.selectors'
import { Store, select } from '@ngrx/store'
import { catchError, debounce, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { RootStoreState } from '../../../../root-store/root-index'
import { root } from '../../../permissions/permissions'
import { ActivatedRoute, Router } from '@angular/router'
import { ConfirmationService, FilterMetadata, LazyLoadEvent } from 'primeng/api'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { AttachmentComponent, EntityConfiguration } from '../../models/entity-configuration'
import { TableData } from '../../models/table-data'
import { Field } from '../../models/field'
import { Entity, EntityData } from '../../models/entity-data'
import { ScriptActionDefinition } from '../../../action-module/models/script-action'
import { EntityService } from '../../services/entity.service'
import { NotificationService } from '../../../../services/notification.service'
import { JmeleonActionsPermissionService } from '../../../permissions/services/jmeleon-actions-permission.service'
import { SettingsService } from '../../../settings/services/settings.service'
import { JmlUrlParamService } from '../../../navigation-state/services/jml-url-param.service'
import { EntityDialogComponent } from '../entity-dialog/entity-dialog.component'
import { FileUploadDialogComponent } from '../../../../components/file-upload-dialog/file-upload-dialog.component'
import { CatalogueService } from '../../../../services/catalogue.service'
import { ScriptResultComponent } from '../../../action-module/components/script-result/script-result.component'
import { Catalogue, CatalogueEntry } from '../../../../models/catalogue'
import { EntityConfigService } from '../../services/entity-config.service'
import EntityUrlsUtils from '../../../../utils/entity-urls.util'
import { ActionService } from '../../../action-module/services/action.service'
import { AdditionalQualifier, SortDirection, TableState } from '../../models/table-state.model'
import { EntityReference, EntityStateService, FilterTileRefreshService } from '../../../../modules/dto'
import { StoredEntityService } from '../../../entity-store-access/services/stored-entity.service'
import { EntityLinkService } from '../../../app-menu/services/entity-link.service'
import { DateFilterComponent } from './filters/date-filter/date-filter.component'
import { FloatFilterComponent } from './filters/float-filter/float-filter.component'
import { SoftFilter } from '../../models/softfilter'
import { Table } from 'primeng/table'
import { TableUtils } from './table-utils'
import { cloneDeep, isEqual, isEqualWith } from 'lodash'
import { BaseNavStateComponent, JmlNavAdapterService } from '../../../navigation-state'
import { DynamicTableState } from './dynamic-table.state'
import { WizardComponent } from '../../../wizard/components/wizard.component'
import { RxStompService } from '../../../../app-config/rx-stomp.service'
import { SelectionService } from '../../services/selection.service'
import { WIZARD_TYPE } from '../../../wizard/models/wizard-data'
import { WizardService } from '../../../wizard/services/wizard.service'
import { SortingDefinition } from '../../models/table-state.model'
import { Favorite, FavoritesService } from '../../../../services/favorites.service'

/** Displays arbitrary entities in a table that can be dynamically configured from the backend using json configs.
 * Supports:
 *  - Displaying entity field values
 *    + Supports the following types:
 *      - String
 *      - int
 *      - float
 *      - boolean
 *      - Currency (A formatted floating point value according to the currency defined in the users settings)
 *      - json
 *      - python (A python script that can be used to execute custom actions on entities)
 *      - multiline (A formatted multiline text field)
 *      - Icon
 *      - Entity Reference (A reference to a entity of the specified type)
 *      - dtoType (A reference to another entity type)
 *      - Catalogue Entry (A reference to a catalogue entry of the specified catalogue)
 *      - Date, DateTime, DatePeriod
 *    + Fields will be displayed under the following conditions
 *      - 'visible' flag is set in entity config
 *      - Current user has read permission for the entity AND the field
 *
 *  - Editing entity fields in a dialog or via inline edit:
 *    + Possible on all displayable types but 'Icon'
 *    + Possible if the following conditions apply for a specific field:
 *      - 'editable' flag is set in entity config
 *      - Current user has write permission for the entity AND the field
 *
 *  - Creating new entities:
 *    + Possible if the create permission is granted for the entity
 *    + Opens entity-dialog in which all editable fields can and all mandatory fields have to be set.
 *
 *  - Lazy loading
 *    + Entity data is loaded on demand when switching pages or when filtering or sorting columns
 *    + The amount of displayed entities per page can be set through a dropdown menu. Inital page size is defined in the configuration
 *
 *  - Filtering and Sorting of table data
 *    + For each visible field the configuration can allow filtering and sorting
 *    + Available filter types:
 *      - Text
 *      - Integer
 *      - MultiSelect
 *      - Boolean
 *      - Date
 *      - Float
 *
 *  - An optional footer can be configured in the entity configuration
 *    + Available types in footer:
 *      - String
 *      - int
 *      - float
 *      - boolean
 *      - Currency
 *
 *  - Horizontal and vertical scrolling as specified in the entity configuration
 *
 *  - Absolute and relative values for column widths aswell as automatic widths as specified in the entity configuration
 *
 *  - Configurable set of actions per entity:
 *    + Each entity can support a subset of the predefined actions:
 *      - UPDATE: Open entity-dialog to edit an entity
 *      - DELETE: Delete the entity after confirming the action through a dialog
 *      - UPLOAD: Upload a file (Only if type equals 'File' or 'FileAttachment')
 *      - DOWNLOAD: Download a file (Only if type equals 'File' or 'FileAttachment')
 *      - ADJUST_STATE_ACTION: Manually adjust the state of a state machine without regarding the possible transitions
 *    + Additionally custom actions that can be defined in the entity configuration can be shown individually for each entry
 *    + If there is more than one custom action they will be displayed inside a hover menu
 *
 *  - Drag and drop reordering of entries through the orderedColumn defined in the entity configuration. The column has to be of type 'int'
 *
 *  - Selection of one ore multiple entities through checkboxes
 *
 * Inputs:
 *  - tableData: Struct containing configuration data for the table
 *  - mainId: Contains the id of the parent entity if available. Needed for tables that display data that is attached to another entity
 *  - mainType: Type of the parent entity
 *  - mainRepr: Representation string of parent entity (Currently not used)
 *  - dblClickCallback: Callback function to invoke when an entity row has been double clicked
 *  - preselectionId: Id of entity that should initially filtered for (Filter can be reset through button in header)
 *  - attachmentCategory: attachment category for tables that display files. Needed in FileUploadDialog
 *  - token: ??
 *  - isRootEntity: defines whether current Entity is a main Entity or not. Default is true.
 *
 * Outputs:
 *  entitySelection: Emits event when an entity has been selected
 *  entityOperation: Emits event when an entity has been edited
 *  entitySelectionListChanged: Emits event when the selection of entities has changed
 *
 */

@Component({
  selector: 'jml-lib-dynamic-table',
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
  providers: [ConfirmationService]
})
export class DynamicTableComponent
  extends BaseNavStateComponent<DynamicTableState>
  implements OnInit, OnDestroy, OnChanges
{
  @Input() tableData: TableData
  @Input() mainId: number
  @Input() mainType: string
  @Input() mainRepr: string
  @Input() ownerId: number
  @Input() ownerType: string
  @Input() dblClickCallback: (data) => any
  @Input() preselectionId: string
  @Input() attachmentCategory: string // FileAttachment category
  @Input() token: string
  @Input() isRootEntity = true
  private $activateTableState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  @Input() set activateTableState(value: boolean) {
    this.$activateTableState.next(value)
  }

  @Output() entitySelection = new EventEmitter()
  @Output() entityOperation = new EventEmitter()
  @Output() entitySelectionListChanged = new EventEmitter()

  dynamicTableRef: ElementRef
  @ViewChildren('dt') dynamicTableRefs: QueryList<ElementRef>
  private _dynamicTable: Table

  @ViewChild('dt')
  set dynamicTable(value: Table) {
    this._dynamicTable = value

    if (!!value) {
      this.subscriptions.push(
        this._filterInitialized$
          .pipe(
            take(1), // prevent reaction to later filter changes in the state
            switchMap(() => this.initWithExistingState$),
            take(1)
          )

          .subscribe((state) => {
            if (this.stateInitialized) {
              return
            }
            this.stateInitialized = true
            if (!!state?.additionalQualifier) {
              this.tableState.additionalQualifier = state.additionalQualifier
            }
          })
      )
    }
  }

  get dynamicTable(): Table {
    return this._dynamicTable
  }

  @ViewChildren('tableHeader') tableHeaders: QueryList<ElementRef>
  @ViewChild(ScriptResultComponent, { static: false }) scriptResultComponentref: ScriptResultComponent

  configuration: EntityConfiguration
  configuration$: Observable<EntityConfiguration>
  subscriptions: Subscription[] = []
  dialogRefs: DynamicDialogRef[] = []

  visibleFields: Field[] = []
  editableFields: Field[] = []
  creationFields: Field[] = []

  loading = true // Needs to be true to prevent ExpressionHasChangedError
  initialLoadCompleted = false
  defaultFilterGate = false
  entityData: EntityData

  lastLazyLoadEvent: LazyLoadEvent
  $loadEvent = new Subject<[LazyLoadEvent, boolean]>()
  filtersInTable = false
  filterOptions = {}
  iconCatalogues = {} // Cache the catalogues for all icon fields so we dont need to retreive them from each individual cell

  root = root

  tableMinWidth: number
  absoluteColumnWidths: number
  freeColumnSpace = 100
  zeroWidthColumns = 0
  crudColumnSpace: number
  columnMinWidths: any
  columnDefaultWidths: any
  columnWidths: any
  actionCount: number
  headerActionCount = 1 // init with 1 since refresh button is always available

  currency: string
  refreshTrigger: Subject<[string, number]>
  rowsPerPageOptions = [10, 25, 50]

  inHorizontalScroll: boolean
  tableState: TableState | undefined = undefined
  menuWrapperElement: HTMLElement
  menuElement: HTMLElement

  executeAction$: Observable<any>
  actionDetails: ScriptActionDefinition
  executeAction = false

  rowClickEventTimer: any
  detail: string
  selectedEntities: any[] | any
  selectedEntityId: number

  selectedEntityDialogId: number
  displayAttachmentDialog: boolean
  attachmentDialogHeader: string

  $navToDetails: Subject<void>
  navToDetails$: Observable<void>
  stateInitialized: boolean = false

  serverCausedRefreshSubscription: Subscription = undefined

  userHasEntityCreationPermission$: Observable<boolean> = EMPTY

  private _filterInitialized$: Subject<void> = new ReplaySubject<void>(1)
  /** unique identifier for primeng-table-state (for filter and page) */
  tableStateKey$: Observable<string>

  additionalQualifierInitialized = false

  // required for showing selected entities count (see SelectionFilterComponent)
  private $selectedCount = new BehaviorSubject<number>(0)
  selectedCount$ = this.$selectedCount.asObservable()
  selectedCount = 0

  constructor(
    private entityService: EntityService,
    private storedEntityService: StoredEntityService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private catalogueService: CatalogueService,
    private notificationService: NotificationService,
    private store$: Store<RootStoreState.State>,
    private japs: JmeleonActionsPermissionService,
    private settingsService: SettingsService,
    private jmlNavigationService: JmlUrlParamService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private entityConfigService: EntityConfigService,
    private route: ActivatedRoute,
    private actionService: ActionService,
    private entityStateService: EntityStateService,
    private filterTileRefreshService: FilterTileRefreshService,
    private entityLinkService: EntityLinkService,
    private webSocketService: RxStompService,
    private selectionService: SelectionService,
    private favoritesService: FavoritesService,
    private wizardService: WizardService,

    jmlNav: JmlNavAdapterService
  ) {
    super(jmlNav, route, router)
    this.setInitialStateGenerator(() => new DynamicTableState())
    this.tableState = {
      pagination: { pageSize: undefined, page: undefined },
      qualifier: undefined,
      additionalQualifier: undefined,
      sortMap: new Map<string, SortingDefinition>()
    }
  }

  ngOnInit() {
    this.stateInitialized = false
    console.log('isRootEntity', this.isRootEntity)
    this.initNavStateSettings(false, true)
    const stateName = `DynTable_${this.tableData.entityType}`

    this.$activateTableState.subscribe((activateState) => {
      if (activateState) {
        this.setComponentIdentifier(stateName)
      }
      super.ngOnInit()
    })

    this.tableStateKey$ = combineLatest([this.urlToken$, this.$activateTableState]).pipe(
      map(([urlToken, activateState]) => (activateState ? `${stateName}_${urlToken}` : null))
    )

    this.$navToDetails = new Subject<void>()
    this.navToDetails$ = this.$navToDetails.asObservable()
    // Get observable for entity configuration from store
    this.configuration$ = this.entityConfigService.getConfig$(this.tableData.entityType)

    this.entityData = new EntityData()

    this.refreshTrigger = new Subject<[string, number]>()
    this.subscriptions.push(
      this.refreshTrigger
        .asObservable()
        .pipe(switchMap(([mainType, mainId]) => this.storedEntityService.getEntity$(mainType, mainId)))
        .subscribe((entity) => {
          // console.log('Inside refreshTrigger: ', entity);
          const index = this.entityData.data.findIndex((data) => data['id'] === entity.fields['id'])
          this.entityData.data[index] = entity.fields
        })
    )

    this.subscriptions.push(this.tableData.triggerRefresh.subscribe(() => this.refreshTableContents()))

    this.userHasEntityCreationPermission$ = this.japs.userHasPermissionForAction(root.dto.$dtoType.create, {
      $dtoType: this.tableData.entityType
    })

    this.settingsService
      .getSetting('CURRENCY')
      .pipe(map((setting) => setting.value))
      .subscribe((currency) => (this.currency = currency))

    this.configuration$
      .pipe(
        filter((config) => {
          if (!config) {
            console.log('[Dynamic-Table] config with name ' + this.tableData.entityType + ' not found!')
          }
          return !!config
        }),
        take(1),

        tap((config) => {
          this.subscriptions.push(
            this.entityOperation.asObservable().subscribe((event) => this.refreshTableContents(event))
          )

          // Add rows per page option to dropdown if defined in config
          if (!this.rowsPerPageOptions.includes(config.rowsPerPage)) {
            this.rowsPerPageOptions.push(config.rowsPerPage)
            this.rowsPerPageOptions.sort()
          }

          this.absoluteColumnWidths = 0
          this.tableMinWidth = config.minWidth === -1 ? 0 : config.minWidth
          this.columnDefaultWidths = new Object()

          let fields: string[] = []
          config.entityAttachments?.forEach((ea) =>
            ea.elements.forEach((element) => {
              const lambda = (component: AttachmentComponent) => {
                if (component.type === 'fieldGroup' && component.fields) {
                  fields = fields.concat(component.fields)
                }
              }
              element.panel?.forEach(lambda)
              element.tabView?.forEach(lambda)
            })
          )

          // this.configuration = config;
        }),

        switchMap((config) => {
          return this.entityConfigService.getEditableFields$(config.type, { checkForReadPermission: true }).pipe(
            tap((fields) => (this.editableFields = fields)),
            map((_) => config)
          )
        }),

        switchMap((config) => {
          return this.entityConfigService.getCreationFields$(config.type).pipe(
            tap((fields) => (this.creationFields = fields)),
            map((_) => config)
          )
        }),

        switchMap((config) => {
          const requestedPermissions = [root.dto.$dtoType.create, root.dto.$dtoType.export]
          return this.japs.userHasPermissionForActions(requestedPermissions, { $dtoType: config.type }).pipe(
            tap((permissions) => {
              this.headerActionCount += permissions[root.dto.$dtoType.create] ? 1 : 0
              // TODO: Remove LogEntry Exception
              this.headerActionCount +=
                permissions[root.dto.$dtoType.export] && !this.mainId && config.type !== 'LogEntry' ? 1 : 0
            }),
            map((_) => config)
          )
        }),

        switchMap((config) => {
          return this.entityConfigService.getVisibleFields$(config.type).pipe(
            tap((fields) => {
              this.visibleFields = fields

              let undefinedWidths = 0

              fields.forEach((field) => {
                // Calculate minWidth if set to auto (-1)
                if (config.minWidth === -1) {
                  if (field.width?.endsWith('px')) {
                    this.tableMinWidth += Number.parseInt(field.width.slice(0, -2))
                  } else {
                    this.tableMinWidth += TableUtils.getDefaultColumnWidth(field.type)
                  }
                }

                if (!field.width) {
                  const defaultWidth = TableUtils.getDefaultColumnWidth(field.type)
                  this.columnDefaultWidths[field.field] = defaultWidth + 'u'
                  undefinedWidths += defaultWidth
                  this.zeroWidthColumns++
                } else {
                  this.columnDefaultWidths[field.field] = field.width
                  this.absoluteColumnWidths += !field.width.endsWith('%') ? Number.parseInt(field.width, 10) : 0
                  this.freeColumnSpace -= field.width.endsWith('%') ? Number.parseInt(field.width, 10) : 0
                }

                this.filtersInTable = field.filterType !== 'none' || this.filtersInTable
              })

              if (this.zeroWidthColumns > 0) {
                Object.keys(this.columnDefaultWidths).forEach((field) => {
                  if (this.columnDefaultWidths[field].endsWith('u')) {
                    this.columnDefaultWidths[field] =
                      this.freeColumnSpace * (Number.parseInt(this.columnDefaultWidths[field]) / undefinedWidths) + '%'
                  }
                })
              }
            }),
            map((_) => config)
          )
        }),

        withLatestFrom(this.tableData.getExternalFilterQualifier$(), this.$activateTableState),
        map(([config, filterQualifier, activateState]) => {
          this.defaultFilterGate = !!filterQualifier || !!config.softFilter || activateState
          return config
        }),

        tap((config) => {
          // console.log('DYN_TABLE config', config);
          this.configuration = config
        }),

        // projects config into single field and iterate each
        switchMap((_) => this.visibleFields),

        filter(
          (field) =>
            Field.isDtoType(field) ||
            ((Field.isCatalogueEntry(field) || Field.isIcon(field) || Field.isMultiselect(field)) &&
              !!field.defaultCatalogue) ||
            Field.isState(field)
        ),

        mergeMap((field) => {
          // get catalogue information for every field of type CatalogueEntry
          let filterOptions: CatalogueEntry[] = []
          if (Field.isDtoType(field)) {
            // get all dtoConfigs for field.type === 'dtoType' (e.g. for Script Actions)
            const configurations$ = this.store$.pipe(select(fromConfigSelectors.selectConfigs))

            return configurations$.pipe(
              filter((configs) => !!configs),
              tap((configs) => {
                let i = 0
                // just add an artificial id for p-multiselect.
                Object.values(configs).map((o) => filterOptions.push({ id: i++, name: o['type'] }))
                this.filterOptions[field.field] = filterOptions
              })
            )
          } else if (Field.isState(field)) {
            return this.entityStateService.getStates(this.configuration.defaultStateMachine).pipe(
              map((statemachine) => statemachine.states),
              tap((states) => {
                states.forEach((state) => {
                  filterOptions.push({
                    id: state.id,
                    name: state.name,
                    icon: state.icon,
                    color: state.color ? state.color : '#000',
                    description: state.name
                  })
                })
                this.filterOptions[field.field] = filterOptions
              })
            )
          } else {
            return this.catalogueService.getCatalogue(field.defaultCatalogue).pipe(
              tap((catalogue: Catalogue) => {
                if (Field.isCatalogueEntry(field) || Field.isMultiselect(field)) {
                  filterOptions = catalogue.values
                } else {
                  catalogue.values.forEach((entry) => {
                    filterOptions.push({
                      id: entry.id,
                      name: entry.name,
                      icon: entry.icon,
                      color: entry.color ? entry.color : '#000',
                      description: entry.description
                    })
                  })
                  this.iconCatalogues[field.defaultCatalogue] = catalogue
                }
                this.filterOptions[field.field] = filterOptions
              })
            )
          }
        })

        // when initialization is done, save the relevant configuration data into the store.
        // TODO REFACTOR => store should only be set upon completion of observable. But this does not fork for DTO Type ScriptAction. No idea why. ;-(
      )
      .subscribe(
        (_) => {
          console.log('NGONINIT SUBSCRIBE', this.configuration.displayName)
          if (this.isRootEntity === true) {
            this.$navToDetails.next()
          }
        },
        (err) => {
          // This is a hack. For DTO Type ScriptAction the observable does not complete. Required data are set correctly though.
          // As a workaround the store will also be set in error case. it should be investigated why DTOType ScriptActions throws an error.
          if (this.isRootEntity === true) {
          }
          console.log('NGONINIT ERROR', this.configuration?.displayName)
          console.log(err)
          console.log('this.configuration', this.configuration)
          console.log('this.visibleFields', this.visibleFields)
          console.log('this.editableFields', this.editableFields)
          console.log('this.creationFields', this.creationFields)
        },
        () => {
          // when observables completes
          console.log('NGONINIT COMPLETE', this.configuration.displayName)
          // save current DTO Type only in case a main entity is loaded

          console.log('this.visibleFields', this.visibleFields)
          console.log('this.editableFields', this.editableFields)
          console.log('this.creationFields', this.creationFields)
        }
      )

    this.subscriptions.push(
      this.navToDetails$
        .pipe(
          take(1),
          tap((c) => console.log('navToDetails$ - c1:  ', c)),
          filter((_) => this.isRootEntity),
          tap((c) => console.log('navToDetails$ - c2:  ', c, this.configuration.viewType)),
          filter((_) => this.configuration.viewType === 'multiPage'),
          withLatestFrom(this.jmlNavigationService.readIdFromQueryParams$(this.route)),
          map(([_, id]) => id),
          tap((c) => console.log('navToDetails$ - c3:  ', c)),
          filter((id) => !!id),
          tap((c) => console.log('navToDetails$ - c4:  ', c))
        )
        .subscribe((id) => {
          const targetUrl = EntityUrlsUtils.getEntityUrlWithIdPathParam(this.configuration.type, id.toString())
          console.log('navigate to: ', targetUrl)
          this.router.navigateByUrl(targetUrl)
        })
    )

    this.subscriptions.push(
      concat(
        this.$loadEvent.asObservable().pipe(take(1)),
        this.$loadEvent.asObservable().pipe(
          filter(
            ([event, debounceBypass]) =>
              debounceBypass ||
              !isEqualWith(event, this.lastLazyLoadEvent, (o1, o2) => {
                // We want to filter out filter events that have previously been sent to the BE.
                // The events contain lambda functions for which direct equality fails.
                // In this case we can ignore them when comparing the events for equality
                if (typeof o1 === 'function' && typeof o2 === 'function') return true
                return undefined
              })
          ),
          // Set high debounce if the filters have changed (e.g. user is typing in filter input).
          // Set low debounce for any other event or if bypass is specified
          debounce(([event, debounceBypass]) =>
            debounceBypass || isEqual(this.lastLazyLoadEvent?.filters, event.filters) ? timer(20) : timer(200)
          )
        )
      )
        .pipe(
          switchMap(([event, _debounceBypass]) => {
            this.lastLazyLoadEvent = cloneDeep(event)
            return this.loadLazy(event)
          })
        )
        .subscribe((data) => {
          this.refreshCount()
          this.entityData = data
          this.loading = false
          this.initialLoadCompleted = true
          this.crudColumnSpace = this.calcCrudColWidth()
          if (this.preselectionId) {
            this.setSelectedEntity(data.data.find((entity) => entity['id'] === +this.preselectionId))
          }

          // Unselect currently selected entry if it is not visible anymore
          if (!data.data.find((entity) => this.selectedEntities && entity['id'] === this.selectedEntities['id'])) {
            this.setSelectedEntity(undefined)
          }
        })
    )

    // get number of selected entities
    this.subscriptions.push(
      this.selectedCount$
        .pipe(
          tap((count) => console.log('newCount', count)),
          tap((count) => (this.selectedCount = count))
        )
        .subscribe()
    )

    this.subscribeToWebSocketForRefresh()
  }

  subscribeToWebSocketForRefresh() {
    if (!!this.serverCausedRefreshSubscription) {
      this.serverCausedRefreshSubscription.unsubscribe()
    }
    this.serverCausedRefreshSubscription = this.webSocketService
      .watchRefresh(this.tableData.entityType)
      .subscribe(() => {
        //console.log('received Server Refresh request for ' + this.tableData.entityType);
        this.refreshTableContents()
      })
  }

  setSelectedEntity(entity: any) {
    this.selectedEntities = entity
    if (!entity) {
      this.selectedEntityId = undefined
    } else {
      this.selectedEntityId = entity['id']
    }
    this.entitySelection.emit(this.selectedEntities)
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
    this.dialogRefs.forEach((dialogRef) => dialogRef?.destroy())
    this.serverCausedRefreshSubscription.unsubscribe()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.mainId && !changes.mainId.firstChange) ||
      (changes.ownerId && !changes.ownerId.firstChange) ||
      (changes.preselectionId && !changes.preselectionId.firstChange)
    ) {
      if ((changes.ownerId || changes.mainId) && !changes.preselectionId) {
        this.preselectionId = null // Reset double click filter if main entity changes
      }

      this.refreshTableContents()
    }
  }

  afterTableInit() {
    this.subscriptions.push(
      this.dynamicTableRefs.changes
        .pipe(
          take(1),
          map((changes) => changes.first.el)
        )
        .subscribe((ref) => {
          this.dynamicTableRef = ref
          this.onTableResize()

          // It seems this part is not working since the scroll event listner never fires. Hence commented out for now.
          // // Add scroll listener for dynamic table scrollable body. When triggered deletes div elements directly inside the body tags. These divs contain form elements which were attached to the body before
          // if (ref.nativeElement.querySelector('div.p-datatable-scrollable-body')) {
          //   ref.nativeElement.querySelector('div.p-datatable-scrollable-body').addEventListener('scroll', (ev: Event) => {
          //     const el = document.body.querySelector('body > div');
          //     // Need to filter out dialogs since entity selection dialogs can also be scrollable but shouldn't be closed on scrolling
          //     if (el && !el.classList.contains("p-dialog-mask") && !el.classList.contains("button-menu-wrapper")) {
          //       el.remove();
          //     }
          //   });
          // }

          ref.nativeElement.addEventListener(
            'click',
            (event: MouseEvent) => {
              if (event.target['id'] === 'cancelRowSelection') {
                // If the target indicates a cancellation of a row selection event, stop its propagation to prevent row selection
                event.preventDefault()
                event.stopPropagation()
                document.dispatchEvent(new MouseEvent('click'))
                // dispatch new event to target
                event.target.dispatchEvent(new CustomEvent('edit'))
              }
            },
            true
          )

          this.subscriptions.push(
            this.tableData
              .getExternalFilterQualifier$()
              .pipe(
                tap((filterQualifier) => {
                  // save additionalQualifier to nav-state
                  this.updateState((state) => {
                    if (!state) {
                      state = new DynamicTableState()
                    }
                    // keep existing nav-state when initializing.
                    // if this is the first execution and the nav-state already contains an additionalQualifier, keep it.
                    if (
                      this.additionalQualifierInitialized ||
                      !!filterQualifier?.hardFilter ||
                      !state?.additionalQualifier
                    ) {
                      state.additionalQualifier = filterQualifier.hardFilter
                    }
                    this.additionalQualifierInitialized = true
                    return state
                  })
                }),
                withLatestFrom(this.configuration$),
                switchMap(([filterQualifier, config]) => {
                  return this.setAdditionalFilters(
                    filterQualifier.hardFilter,
                    !!filterQualifier.softFilter
                      ? filterQualifier.softFilter
                      : new SoftFilter(undefined, config.softFilter)
                  ).pipe(map(() => filterQualifier))
                })
              )
              .subscribe(() => {
                // Table will refresh itself if filters have changed. We want to manually refresh here though since we want to bypass the debounce
                let lazyLoadEvent: LazyLoadEvent = cloneDeep(this.lastLazyLoadEvent)
                lazyLoadEvent.filters = <{ [s: string]: FilterMetadata }>this.dynamicTable.filters
                this.loadLazyDebounce(lazyLoadEvent, true)
              })
          )
        })
    )

    // Capture click events on table headers. The filter inputs need to stop the propagation of events to not trigger a sort event.
    // We thus need to simulate mouse click on document level to close inline edit controls when clicking on a filter control.
    this.subscriptions.push(
      this.tableHeaders.changes.pipe(take(1)).subscribe((changes) => {
        changes._results.forEach((ref: ElementRef) => {
          ref.nativeElement.addEventListener(
            'click',
            (event: MouseEvent) => {
              document.dispatchEvent(new MouseEvent('click'))
            },
            true
          )
        })
      })
    )
  }

  async rowSelect() {
    if (!this.tableData.showSelectList) {
      // only handle single row click if it is not a double click
      this.rowClickEventTimer = setTimeout(() => {
        this.handleRowSelect()
      }, 300)
    } else {
      this.entitySelectionListChanged.emit(
        (<any[]>this.selectedEntities).filter((entity) => !!entity).map((entity) => entity.id)
      )
    }
  }

  handleRowSelect() {
    this.setSelectedEntity(this.selectedEntities)
  }

  rowReorder(event) {
    // Need to use dropIndex since entityData has already been reordered
    const entity = Object.assign({}, this.entityData.data[event.dropIndex])
    // index from event is relative to the current page; add page * pageSize; add 1 since backend starts with index 1
    const newIndex = event.dropIndex + (this.tableState.pagination.page - 1) * this.tableState.pagination.pageSize + 1
    entity[this.configuration.orderedColumn] = newIndex
    this.storedEntityService
      .updateEntity$(this.configuration.fields, this.configuration.type, entity['id'], {
        [this.configuration.orderedColumn]: newIndex
      })
      .subscribe(() => {
        if (this.configuration.refreshOnModify) {
          this.entityOperation.emit(null)
        }
      })
  }

  async refreshTableContents(event?: Event) {
    // refresh triggered by filterTile should be ignored / event type click is triggered by refresh button click
    if (
      event?.type == 'delete' ||
      event?.type == 'create' ||
      event?.type == 'performAction' ||
      event?.type == 'click'
    ) {
      this.filterTileRefreshService.triggerFilterTileRefresh()
    }
    // console.log('this.lastLazyLoadEvent', this.lastLazyLoadEvent);
    this.loadLazyDebounce(this.lastLazyLoadEvent, true)
  }

  trackByFunction = (index, item) => {
    return item ? item.id : null
  }

  loadLazyDebounce(event: LazyLoadEvent, debounceBypass = false) {
    if (!event) return

    /* the primeng table does not support default filters, which means that we need to apply them after the table has been initialized.
     * To prevent the table from displaying the unfiltered results we need to skip the initial lazy load if default filters have been defined.
     */
    if (this.defaultFilterGate) {
      this.defaultFilterGate = false
      this.lastLazyLoadEvent = event
      return
    }

    this.$loadEvent.next([event, debounceBypass])
  }

  onSort(sortDirection: SortDirection, field: string) {
    // save sorting definition
    if (sortDirection !== SortDirection.NONE) {
      this.tableState.sortMap.set(field, { sortDirection: sortDirection, sortingOrder: undefined })
    } else {
      this.tableState.sortMap.delete(field)
    }
    this.tableState.pagination.page = 1 // when sorting always jump to page 1

    this.refreshTableContents()
  }

  // set sortingOrder for multiSort and
  // compose comma separated query string for backend
  private composeSortQueryString(): string | undefined {
    let tmpSortQueryString = ''

    // convert map to array to have the index available
    Array.from(this.tableState.sortMap).forEach(([key, value], idx) => {
      value.sortingOrder = idx + 1 // calculate sorting order for multisort
      tmpSortQueryString = `${tmpSortQueryString},${value.sortDirection}('${key}')`
    })
    const sortQueryString = tmpSortQueryString.substring(1) //remove first comma

    return sortQueryString !== '' ? sortQueryString : undefined
  }

  // make sorting definition accessible from template
  getSortingDefinition(field: string): SortingDefinition | undefined {
    return this.tableState.sortMap?.get(field)
  }

  isMultiColumSort(): boolean {
    return this.tableState.sortMap?.size > 1
  }

  loadLazy(event: LazyLoadEvent) {
    this.loading = true
    let qualifier = ''

    this.visibleFields.forEach((field) => {
      if (event.filters[field.field]) {
        const value: any = event.filters[field.field].value
        if (value === null) {
          return
        }
        let formattedValue: string

        if (field.filterType === 'text') {
          if (value === '') {
            return
          }
          const filterContent: string = value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
          formattedValue = `'${filterContent}'`
        } else if (field.filterType === 'multiSelect') {
          if (Array.isArray(value) && value.length === 0) {
            return
          }
          formattedValue =
            field.type === 'dtoType'
              ? `[${value.map((v) => `'${v.name}'`).join(',')}]` // evaluates to e.g. ['Sample', 'ScriptAction'] (only for type dtoType)
              : `[${value.map((v) => v.id).toString()}]` // evaluates to e.g. [12, 30, 56]
        } else if (field.filterType === 'integer') {
          if (value === '-') {
            return
          }
          formattedValue = value.toString()
        } else if (field.filterType === 'bool') {
          formattedValue = value ? 'true' : 'false'
        } else if (field.filterType === 'date') {
          formattedValue = DateFilterComponent.formatFilterValue(value.matchMode, value.dateSingle, value.dateRange)
          if (!formattedValue) return
        } else if (field.filterType === 'floating') {
          formattedValue = FloatFilterComponent.formatFilterValue(value.matchMode, value.floatFrom, value.floatTo)
          if (!formattedValue) return
        } else {
          formattedValue = `'${value.toString()}'`
        }

        if (field.filterType === 'selection') {
          // we use a bool filter control so that value is either true or false
          qualifier += value ? 'SELECTED(),' : 'UNSELECTED(),'
        } else {
          qualifier += `${field.filterType}('${field.field}',${formattedValue}),`
        }
      }
    })

    if (!!this.preselectionId) {
      qualifier += `integer('id',${this.preselectionId}),`
    }

    let page = 1
    if (event.rows && !this.preselectionId) {
      page = event.first / <number>event.rows + 1
    }

    const sorting = this.tableState.sortMap
    // save current table config for later user. Required for csv export.
    this.tableState = {
      pagination: { pageSize: event.rows, page: page },
      qualifier: qualifier,
      additionalQualifier: this.tableState.additionalQualifier,
      sortMap: sorting
    }
    if (this.tableData.dataSource === undefined) {
      return this.storedEntityService.filter$(
        this.configuration.fields,
        this.tableData.entityType,
        this.tableState.pagination.page,
        this.tableState.pagination.pageSize,
        this.mainId,
        this.tableState.qualifier,
        this.composeSortQueryString(),
        this.tableState.additionalQualifier,
        this.ownerType,
        this.ownerId
      )
    } else {
      return this.tableData.dataSource
    }
  }

  setAdditionalFilters(additionalQualifier: AdditionalQualifier, softFilterExpression: SoftFilter): Observable<any> {
    this.tableState.additionalQualifier = additionalQualifier

    if (!!softFilterExpression?.softFilterExpression) {
      return this.entityService.evalSoftFilter$(softFilterExpression).pipe(
        take(1),
        tap((evaluatedFilter) => {
          this.visibleFields.forEach((field) => {
            let filterExpression = evaluatedFilter[field.field]
            if (field.field in evaluatedFilter) {
              if (field.filterType === 'multiSelect') {
                filterExpression = []
                evaluatedFilter[field.field].map((id) => {
                  filterExpression.push({ id: id })
                })
                this.dynamicTable.filter(filterExpression, field.field, undefined)
              } else {
                this.dynamicTable.filter(evaluatedFilter[field.field], field.field, undefined)
              }
            } else {
              if (!!this.dynamicTable.filters[field.field]) {
                // console.log('reset filter with fields', this.dynamicTable.filters[field.field]['value']);
                this._filterInitialized$.next()
                this.dynamicTable.filters[field.field]['value'] = null
              }
            }
          })
        })
      )
    } else if (this.initialLoadCompleted) {
      for (const filter in this.dynamicTable.filters) {
        // console.log('reset filter in tablefilters', filter);
        this.dynamicTable.filters[filter]['value'] = null
      }
    }
    this._filterInitialized$.next()
    return of(null)
  }

  exportTable(exportType: string) {
    this.entityService
      .exportTable(
        this.tableData.entityType,
        this.tableState.pagination.page,
        -1,
        this.mainId,
        this.tableState.qualifier,
        this.composeSortQueryString(),
        exportType
      )
      .subscribe((data) => {
        // get content type, (e.g. application/pdf) and fileName
        const type = data.headers.get('content-type')
        let fileName = data.headers.get('content-filename')
        fileName = fileName.substring(1, fileName.length - 1)
        // define blob and set content type
        const blob = new Blob([data.body], { type: type })
        // create temporary download link and click it
        const downloadURL = URL.createObjectURL(blob)
        let link = document.createElement('a')
        link.href = downloadURL
        link.download = fileName
        link.click()
      })
  }

  createEntity() {
    if (!this.tableData.entitySelectionDialog && !!this.selectedEntities) {
      this.setSelectedEntity(undefined)
    }
    if (!this.configuration.customCreation || this.configuration.customCreation.creationMode === 'createEntity') {
      const dialogContext: { buttonTxt: string; headerTxt: string; desc: string } = TableUtils.getEntityDialogContext(
        this.configuration,
        'create'
      )
      const $onChange = new Subject()
      const $enableForm = new Subject<void>()
      const dialogRef = this.dialogService.open(EntityDialogComponent, {
        data: {
          description: dialogContext.desc,
          fields: this.creationFields,
          configType: this.configuration.type,
          mainId: this.mainId,
          ownerType: this.ownerType,
          ownerId: this.ownerId,
          buttonText: dialogContext.buttonTxt,
          desc: dialogContext.desc,
          onChange: $onChange,
          enableForm: $enableForm.asObservable()
        },
        header: dialogContext.headerTxt,
        draggable: true,
        width: '500px'
      })
      this.dialogRefs.push(dialogRef)

      this.subscriptions.push(
        $onChange
          .asObservable()
          .pipe(
            switchMap((fields: Field[]) => {
              if (fields) {
                return this.storedEntityService.createEntity$(
                  this.configuration.fields,
                  this.configuration.type,
                  fields
                )
              } else {
                return EMPTY
              }
            }),
            catchError((error, caught) => {
              console.error('[Dynamic-Table] Method createEntity failed!', error)
              $enableForm.next()
              return caught
            }),
            take(1)
          )
          .subscribe((entity) => {
            if (!!entity && this.configuration.selectEntityAfterCreation) {
              this.preselectionId = this.configuration.viewType === 'multiPage' ? entity.fields['id'] : null
              this.handleDoubleClickTableRow(entity.fields)
            }
            this.notificationService.addSuccessNotification(
              `${this.configuration.singleDisplayName} '${entity.fields['_repr_']}' wurde hinzugefügt.`
            )
            this.entityOperation.emit(new CustomEvent('create'))
            dialogRef.close()
          })
      )
    } else if (this.configuration.customCreation.creationMode === 'executeAction') {
      this.performActionOnCreate(this.configuration.customCreation.creationAction.action)
    } else if (this.configuration.customCreation.creationMode === 'executeWizard') {
      this.executeWizard(
        this.configuration.customCreation.creationAction?.action,
        undefined,
        undefined,
        WIZARD_TYPE.CREATE_DTO
      )
    }
  }

  updateEntity(rowData: Field[]) {
    // when changing a main entity and another entity is selected then unselect it
    if (
      !this.tableData.entitySelectionDialog &&
      this.selectedEntities &&
      (<any>this.selectedEntities)['id'] !== rowData['id']
    ) {
      this.selectedEntities = undefined
    }

    if (!this.configuration.customUpdate || this.configuration.customUpdate.updateMode === 'updateEntity') {
      const dialogContext: { buttonTxt: string; headerTxt: string; desc: string } = TableUtils.getEntityDialogContext(
        this.configuration,
        'update'
      )
      const $onChange = new Subject()
      const $enableForm = new Subject<void>()
      const dialogRef = this.dialogService.open(EntityDialogComponent, {
        data: {
          description: dialogContext.desc,
          entity: rowData,
          fields: this.editableFields.filter((f) => f.showOnUpdate),
          configType: this.configuration.type,
          mainId: this.mainId,
          buttonText: dialogContext.buttonTxt,
          desc: dialogContext.desc,
          onChange: $onChange,
          enableForm: $enableForm.asObservable()
        },
        header: dialogContext.headerTxt,
        draggable: true,
        width: '500px'
      })
      this.dialogRefs.push(dialogRef)

      this.subscriptions.push(
        $onChange
          .pipe(
            switchMap((fields: Field[]) =>
              fields
                ? this.storedEntityService.updateEntity$(
                    this.configuration.fields,
                    this.configuration.type,
                    rowData['id'],
                    fields,
                    rowData['_lastUpdate_']
                  )
                : EMPTY
            ),
            catchError((error, caught) => {
              console.error('[Dynamic-Table] Method updateEntity failed!', error)
              $enableForm.next()
              return caught
            }),
            take(1)
          )
          .subscribe((entity) => {
            // refresh entityData => data shown in dynamic table is updated directly
            const index = this.entityData.data.findIndex((data) => data['id'] === entity.fields['id'])
            this.entityData.data[index] = entity.fields
            this.notificationService.addSuccessNotification(
              `${this.configuration.singleDisplayName} '${entity.fields['_repr_']}' wurde aktualisiert.`
            )
            if (this.configuration.refreshOnModify) {
              this.entityOperation.emit(new CustomEvent('update'))
            }
            dialogRef.close()
          })
      )
    } else if (this.configuration.customUpdate.updateMode === 'executeWizard') {
      this.executeWizard(this.configuration.customUpdate.updateAction?.action, rowData['id'], WIZARD_TYPE.UPDATE_DTO)
    }
  }

  // covers multiple scenarios:
  // 1a) File entity: create new file:                ownerId: undefined   ownerType: undefined   fileId: undefined
  // 1b) File entity: update existing file:           ownerId: undefined   ownerType: undefined   fileId: set
  // 2a) FileAttachment entity: create new file:      ownerId: set         ownerType: set         fileId: undefined
  // 2b) FileAttachment entity: update existing file: ownerId: set         ownerType: set         fileId: set
  // FileUploadDialogComponent decides depending on these parameters what to do (attach yes/no, update/create)
  uploadFile(header: string, ownerId?: number, ownerType?: string, fileId?: number) {
    if (!this.tableData.entitySelectionDialog && this.selectedEntityId !== fileId) {
      this.setSelectedEntity(undefined)
    }

    const dialogRef = this.dialogService.open(FileUploadDialogComponent, {
      data: {
        catalogueName: 'FILE_TYPES',
        catalogueDisplayName: 'Dateityp',
        ownerId: ownerId,
        ownerType: ownerType,
        fileId: fileId,
        attachmentCategory: this.attachmentCategory
      },
      header: header,
      draggable: true,
      width: 'fit-content'
    })

    this.subscriptions.push(dialogRef.onClose.subscribe(() => this.refreshTableContents()))
  }

  deleteEntity(rowData: Field[]) {
    if (
      !this.tableData.entitySelectionDialog &&
      !!this.selectedEntities &&
      <any>this.selectedEntities['id'] !== rowData['id']
    ) {
      this.selectedEntities = undefined
    }
    this.confirmationService.confirm({
      message: 'Sind Sie sicher, dass Sie diesen Eintrag löschen wollen?',
      header: 'Eintrag löschen',
      key: 'global',
      accept: () => {
        this.subscriptions.push(
          this.storedEntityService.deleteEntity$(this.configuration.type, rowData['id']).subscribe(() => {
            if (!this.tableData.entitySelectionDialog && rowData['id'] === this.selectedEntityId) {
              this.setSelectedEntity(undefined)
            } // do not unselect in entitySelectionDialog
            this.notificationService.addSuccessNotification(
              `${this.configuration.singleDisplayName} '${rowData['_repr_']}' wurde gelöscht.`
            )
            this.entityOperation.emit(new CustomEvent('delete'))
          })
        )
      }
    })
  }

  doubleClickTableRow(event: MouseEvent, data: any) {
    if (this.tableData.entitySelectionDialog) {
      this.entitySelection.emit(<Entity>data)
      return // Dont handle double clicks in entity selection dialog
    }
    // dont handle double click in input elements
    if (event.target['tagName'] === 'INPUT') {
      return
    }
    // stop single click handling
    clearTimeout(this.rowClickEventTimer)
    this.rowClickEventTimer = undefined
    // handle double click
    this.handleDoubleClickTableRow(data)
  }

  handleDoubleClickTableRow(data: any) {
    if (this.configuration.viewType === 'singlePage') {
      if (typeof this.dblClickCallback === 'undefined') {
        console.log('no handler defined for DblClick events')
      } else {
        this.dblClickCallback(data)
      }
      if (this.configuration.activateFilterViaDoubleClick) {
        if (!!this.preselectionId) {
          this.preselectionId = null
        } else {
          this.preselectionId = data.id
          if (this.isRootEntity) {
            this.entityLinkService
              .getEntityLinkData$({ type: this.tableData.entityType, id: data.id })
              .pipe(take(1))
              .subscribe((result) => {
                this.router.navigate([result.url], { queryParams: result.queryParams })
              })
          }
        }
        this.refreshTableContents()
      }
    } else if (this.configuration.viewType === 'multiPage') {
      this.subscriptions.push(
        this.entityLinkService
          .getEntityLinkData$({ type: this.tableData.entityType, id: data.id })
          .pipe(take(1))
          .subscribe((result) => {
            // console.log('navigating to: ', result.url, result.queryParams);
            this.router.navigate([result.url], result.generic ? { queryParams: result.queryParams } : undefined)
          })
      )
    } else if (this.configuration.viewType === 'attachmentDialog') {
      this.selectedEntityDialogId = data.id
      this.attachmentDialogHeader = data._repr_
      this.displayAttachmentDialog = true
    }
  }

  // Check if non-standard actions are present. These actions will be displayed inside a seperate menu
  shouldDisplayActionMenu(rowData) {
    return (
      rowData._actions_.filter(
        (action) =>
          action !== 'UPDATE' &&
          action !== 'DELETE' &&
          action !== 'UPLOAD' &&
          action !== 'DOWNLOAD' &&
          action !== 'ADJUST_STATE_ACTION'
      ).length > 1
    )
  }

  // TODO for Uwe: what about this change??
  isInlineEditable(field: Field, entityId: number): boolean {
    const ignoreSelection = true
    return (
      this.tableData.inlineEdit &&
      field.editable &&
      this.editableFields.some((f) => f.field == field.field) &&
      (ignoreSelection || this.selectedEntityId === entityId)
    )
  }

  public getEntityData(): EntityData {
    return this.entityData
  }

  downloadUrl(id: number): string {
    return UrlCollection.Files.generateDownloadUrl(id)
  }

  onTableResize() {
    const wasUndefined = !this.columnWidths

    if (
      this.tableMinWidth &&
      this.dynamicTableRef.nativeElement.firstChild.offsetWidth !== 0 &&
      this.dynamicTableRef.nativeElement.firstChild.offsetWidth < this.tableMinWidth
    ) {
      if (!this.columnMinWidths) {
        // Calculate min widths for each visible field
        this.columnMinWidths = new Object()
        if (this.crudColumnSpace === undefined) {
          this.crudColumnSpace = this.calcCrudColWidth()
        }
        const usableWidth = this.tableMinWidth - this.crudColumnSpace - this.absoluteColumnWidths

        this.configuration.fields.forEach((field) => {
          this.subscriptions.push(
            this.japs
              .userHasPermissionForAction(root.dto.$dtoType.$dtoField.read, {
                $dtoType: this.configuration.type,
                $dtoField: field.field
              })
              .subscribe((userHasReadPermission) => {
                if (userHasReadPermission && field.visible) {
                  let columnWidth
                  if (!field.width) {
                    columnWidth = TableUtils.getDefaultColumnWidth(field.type) + 'px'
                  } else if (field.width.endsWith('px')) {
                    columnWidth = field.width
                  } else {
                    columnWidth = Math.floor((Number.parseInt(field.width, 10) * usableWidth) / 100.0) + 'px'
                  }
                  this.columnMinWidths[field.field] = columnWidth
                }
              })
          )
        })
      }

      if (!this.inHorizontalScroll || wasUndefined) {
        this.columnWidths = this.columnMinWidths
        this.inHorizontalScroll = true
      }
    } else {
      if (this.inHorizontalScroll || wasUndefined) {
        this.columnWidths = this.columnDefaultWidths
        this.inHorizontalScroll = false
      }
    }

    if (wasUndefined) {
      this.cd.detectChanges()
    }
  }

  // width required to display action icons, per Icon 33.25px required, additional offset of 2x9.75px for padding
  // either the number of actions per entity or the number of available header action is taken dpending which is more
  calcCrudColWidth(): number {
    // If the filter is active we need space for an additional button in the header
    const filterActive = !!this.preselectionId ? 1 : 0
    if (this.entityData.maxActionNumber === undefined) {
      return (this.headerActionCount + filterActive) * 33.25 + 19.5
    }
    return Math.max(this.entityData.maxActionNumber, this.headerActionCount + filterActive) * 33.25 + 19.5
  }

  // gets the final payload depending on the actionDetails
  performAction(actionHrid: string, dtoType: string, entityId: number, actionButton?) {
    if (actionButton) actionButton.inProgress = true
    this.actionService.performAction(actionHrid, { dtoType, id: entityId }, undefined, (success) => {
      if (success) {
        this.refreshTableContents(new CustomEvent('performAction'))
      }
      if (actionButton) actionButton.inProgress = false
    })
  }

  performActionOnCreate(actionHrid: string) {
    const mainRef: EntityReference = { dtoType: this.mainType, id: this.mainId }
    // this is a bit fishy. as the EntityDialogComponent needs an entityReference, we will give it the mainRef instead..
    this.actionService.performAction(actionHrid, mainRef, mainRef, (success) => {
      if (success) {
        this.refreshTableContents(new CustomEvent('performAction'))
      }
    })
  }

  executeWizard(wizardHrid: string, entityId?: number, fieldName?: string, wizType?: WIZARD_TYPE) {
    this.wizardService.openWizard(undefined, wizardHrid, this.configuration.type, entityId, fieldName, wizType)
  }

  // Appends button menu wrapper to the body and sets absolute position relative to body to the correct position
  buttonMenuOver(menu: HTMLElement) {
    const buttonRect = menu.getBoundingClientRect()
    this.menuElement = menu
    this.menuWrapperElement = menu.querySelector('div.button-menu-wrapper')
    if (this.menuWrapperElement !== null) {
      document.body.append(this.menuWrapperElement)
      // document.body.clientWidth = real width of body (we need to exclude the scrollbar)
      // We need to add 16px since the body has a margin of 16px which is not included in clientWidth
      // Additionally 0.5rem get subtracted to compensate the outer padding of the menu so that the buttons align horizontally with the ones in the table
      this.menuWrapperElement.setAttribute(
        'style',
        'display: inherit; right: calc(' +
          (document.body.clientWidth - buttonRect.right + window.scrollX) +
          'px - 0.5rem); top: ' +
          (buttonRect.y + window.scrollY) +
          'px;'
      )
    }
  }

  // Appends button menu wrapper back to the button in the the crud column and hides it
  buttonMenuOut(event: MouseEvent) {
    const relatedTarget = event?.relatedTarget
    // Check if event originated from tooltip. If true ignore since we dont want to hide the menu when hovering tooltips of buttons inside it.
    if (
      relatedTarget &&
      (relatedTarget['classList']?.contains('p-tooltip') || relatedTarget['classList']?.contains('p-tooltip-text'))
    ) {
      return
    }

    if (this.menuWrapperElement) {
      this.menuElement.append(this.menuWrapperElement)
      this.menuWrapperElement.setAttribute('style', 'display: none')
      this.menuWrapperElement = null
      this.menuElement = null
    }
  }

  completeCellEdit(event) {
    console.log('dynamicTable complete cell edit')
    const eventRowData = event.data
    const fieldName = event.field
    this.updateField(eventRowData['id'], fieldName, eventRowData[fieldName])
  }

  updateField(id: number, fieldName: string, value: any) {
    const index = this.entityData.data.findIndex((data) => data['id'] === id)
    const lastUpdate = this.entityData.data[index]['_lastUpdate_']

    // the construct {[fieldName]: value ?? null} is important as an undefined value would prevent sending the property fieldName at all
    this.subscriptions.push(
      this.storedEntityService
        .updateEntity$(
          this.configuration.fields,
          this.tableData.entityType,
          id,
          { [fieldName]: value ?? null },
          lastUpdate
        )
        .subscribe(
          (entity) => {
            // just to be sure - user might have done another action (scrolling, new page) so that the index needs to be searched again
            if (entity?.fields) {
              const indexAfterUpdate = this.entityData.data.findIndex((data) => data['id'] === entity.fields['id'])
              this.entityData.data[indexAfterUpdate] = entity.fields
            }
            if (this.configuration.refreshOnModify) {
              this.entityOperation.emit(null)
            }
            // for field isSelected additionally the count needs to be refreshed
            if (fieldName === 'isSelected') {
              this.refreshCount()
            }
          },
          (error) => this.refreshTableContents()
        )
    )
  }

  // retrieves count of selected entities from backend for field "isSelected"
  refreshCount() {
    this.subscriptions.push(
      this.selectionService
        .getSelectedCount(this.configuration.type)
        .pipe(tap((res) => this.$selectedCount.next(res['count'])))
        .subscribe()
    )
  }

  resetFilter(event?: Event) {
    if (!!this.preselectionId) {
      this.preselectionId = undefined
      this.jmlNavigationService.clearId(this.router)
      this.refreshTableContents()
      // this.router.navigate([])
    }
  }

  _isCatalogueEntry(field: Field): boolean {
    return Field.isCatalogueEntry(field)
  }

  _isEntityReference(field: Field): boolean {
    return Field.isEntityReference(field)
  }

  _isMultiselect(field: Field): boolean {
    return Field.isMultiselect(field)
  }

  // columnFilter(event?: Event){

  // // saving active filters to nav-state
  // const filterValues = Object
  //     .keys(event['filters'])
  //     .map(key => new DynamicTableStateItem(key, event['filters'][key].value, event['filters'][key]['matchMode']))
  //     .filter(item => item.value !== undefined && item.value !== null && item.value !== '')
  //     .map(item => {
  //         return item;
  //     })
  //     // preventing empty date filter from being saved
  //     .filter(item => (!item.value.hasOwnProperty('dateRange') || !!item.value['dateRange'] || !!item.value['dateSingle']))
  //     .map(item => {
  //         return item;
  //     });

  // console.log('writing new state: ', filterValues);

  // // const state = new DynamicTableState();
  // // state.filterValues = filterValues;
  // // this.saveState(state);
  // this.updateState((state) => {
  //     console.log('updateState - filterValues', state);
  //     if (!state){
  //         state = new DynamicTableState();
  //     }
  //     state.filterValues = filterValues;
  //     console.log('writing new state2: ', state);
  //     return state;
  // })

  // }

  isFavorite(id: string): boolean {
    return this.favoritesService.isFavorite(id)
  }

  private generateRouteForFavorite(type: string): string {
    // route generation
    return `/entity-link/${type}`
  }

  toggleFavorite(event: Event): void {
    // prevent other click handlers from being triggered
    event.stopPropagation()

    if (!this.configuration) return

    // read icon from route
    const routeIcon = this.findIconForEntityType(this.configuration.type)

    const favorite: Favorite = {
      id: this.configuration.type,
      name: this.configuration.displayName,
      // fallback-Icon
      icon: routeIcon || 'pi pi-list',
      route: this.generateRouteForFavorite(this.configuration.type),
      dtoType: this.configuration.type
    }

    this.favoritesService.toggleFavorite(favorite)
  }

  private readonly entityIconMap: { [key: string]: string } = {
    SecurityRole: 'fa fa-users',
    ScriptIde: 'fa fa-code',
    DtoConfigEditor: 'fa fa-cog',
    DtoEntities: 'fa fa-database',
    RoleManagement: 'fa fa-users',
    Dashboard: 'fa fa-home',
    UserSettings: 'fa fa-user-edit',
    SystemSettings: 'fa fa-cog'
  }

  // helper-method to find the icon for an entity type
  private findIconForEntityType(entityType: string): string | undefined {
    // 1. Versuch: Icon aus aktueller Route
    if (this.route.snapshot.data['entityType'] === entityType && this.route.snapshot.data['icon']) {
      return this.route.snapshot.data['icon']
    }

    // 2. Versuch: Icon aus Router-Konfiguration
    const routes = this.router.config
    const layoutRoute = routes.find((r) => r.path === '')
    if (layoutRoute && layoutRoute.children) {
      const entityRoute = layoutRoute.children.find((r) => r.data && r.data['entityType'] === entityType)
      if (entityRoute && entityRoute.data && entityRoute.data['icon']) {
        return entityRoute.data['icon']
      }
    }

    // 3. Fallback: Icon aus hart hinterlegter Map
    return this.entityIconMap[entityType]
  }
}
